import { CrwdIcon } from '@crewdle/ui-kit';
/**
 * The app's icon registry for @crewdle/ui-kit's <crwd-icon>.
 *
 * Lucide SVGs (inherited from the old <app-icon> polyfill) registered under
 * BOTH their Lucide keys and the historical ionicon alias names, so templates
 * (including dynamic [name] bindings) keep their existing names. Register once
 * with provideCrwdIcons(APP_ICONS) in the root module.
 */


import {
  heroAdjustmentsHorizontal, heroArchiveBox, heroArrowDownTray, heroArrowPath, heroArrowRightStartOnRectangle, heroArrowUp, heroArrowUturnLeft,
  heroArrowsPointingOut, heroBars2, heroBars3BottomLeft, heroBeaker, heroBolt, heroBookOpen, heroBookmark, heroBookmarkSlash, heroCamera,
  heroChatBubbleLeftRight, heroChatBubbleOvalLeftEllipsis, heroCheck, heroCheckCircle, heroChevronDown, heroChevronLeft, heroChevronRight, heroChevronUp,
  heroClipboard, heroClipboardDocument, heroClock, heroComputerDesktop, heroDocument, heroDocumentDuplicate, heroDocumentText,
  heroEllipsisHorizontal, heroExclamationTriangle, heroEye, heroEyeSlash, heroFolder,
  heroFolderOpen, heroFolderMinus, heroFolderPlus, heroGlobeAlt,
  heroInformationCircle, heroLightBulb, heroLinkSlash, heroMagnifyingGlass,
  heroPaperClip, heroPause, heroPencil, heroPencilSquare, heroPlay, heroPlus, heroShare, heroSparkles, heroSquares2x2, heroStop, heroTrash,
  heroUserCircle, heroWindow, heroXMark,
  // Added for the connect Lucide→Heroicons standardization (2026-09):
  heroAcademicCap, heroArrowTopRightOnSquare, heroArrowUpTray, heroArrowsPointingIn, heroArrowsRightLeft,
  heroBanknotes, heroBell, heroBellSlash, heroBriefcase, heroBuildingOffice, heroBuildingStorefront,
  heroCalculator, heroCalendar, heroChartBar, heroChatBubbleOvalLeft, heroClipboardDocumentCheck,
  heroCodeBracket, heroCog6Tooth, heroCpuChip, heroCreditCard, heroEllipsisVertical, heroEnvelope,
  heroEnvelopeOpen, heroExclamationCircle, heroFaceSmile, heroListBullet, heroLockClosed, heroMegaphone,
  heroMusicalNote, heroNoSymbol, heroNumberedList, heroPaperAirplane, heroPauseCircle, heroPhoto,
  heroPlayCircle, heroPlusCircle, heroReceiptPercent, heroRectangleStack, heroServer, heroShieldCheck,
  heroShoppingCart, heroSwatch, heroUserPlus, heroUsers, heroVideoCamera, heroWallet, heroWrench, heroXCircle,
  // Marketplace industries (2026-10-23):
  heroGift, heroHeart, heroScale, heroTicket, heroWrenchScrewdriver,
  } from '@crewdle/ui-kit/icons';

/* eslint-disable @typescript-eslint/naming-convention, max-len */
const LUCIDE_ICONS: Record<string, string> = {
  'chevron-left': '<path d="m15 18-6-6 6-6" />',
  'chevron-right': '<path d="m9 18 6-6-6-6" />',
  'arrow-left': '<path d="m12 19-7-7 7-7" /> <path d="M19 12H5" />',
  'arrow-right': '<path d="M5 12h14" /> <path d="m12 5 7 7-7 7" />',
  'arrow-left-right': '<path d="M8 3 4 7l4 4" /> <path d="M4 7h16" /> <path d="m16 21 4-4-4-4" /> <path d="M20 17H4" />',
  'chevron-down': '<path d="m19.5 8.25-7.5 7.5-7.5-7.5" />',
  'x': '<path d="M18 6 6 18" /> <path d="m6 6 12 12" />',
  'x-circle': '<circle cx="12" cy="12" r="10" /> <path d="m15 9-6 6" /> <path d="m9 9 6 6" />',
  'check': '<path d="m4.5 12.75 6 6 9-13.5" />',
  'check-check': '<path d="m2 12 5 5L18 6" /><path d="m7 12 5 5 10-11" />',
  'check-circle': '<path d="M21.801 10A10 10 0 1 1 17 3.335" /> <path d="m9 11 3 3L22 4" />',
  'plus': '<path d="M5 12h14" /> <path d="M12 5v14" />',
  'box': '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /> <path d="m3.3 7 8.7 5 8.7-5" /> <path d="M12 22V12" />',
  'search': '<path d="m21 21-4.34-4.34" /> <circle cx="11" cy="11" r="8" />',
  'copy': '<rect width="14" height="14" x="8" y="8" rx="2" ry="2" /> <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />',
  'download': '<path d="M12 15V3" /> <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /> <path d="m7 10 5 5 5-5" />',
  'refresh-cw': '<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /> <path d="M21 3v5h-5" /> <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /> <path d="M8 16H3v5" />',
  'send': '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" /> <path d="m21.854 2.147-10.94 10.939" />',
  'paperclip': '<path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />',
  'share': '<path d="M12 2v13" /> <path d="m16 6-4-4-4 4" /> <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />',
  'share-2': '<circle cx="18" cy="5" r="3" /> <circle cx="6" cy="12" r="3" /> <circle cx="18" cy="19" r="3" /> <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" /> <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />',
  'external-link': '<path d="M15 3h6v6" /> <path d="M10 14 21 3" /> <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />',
  'log-out': '<path d="m16 17 5-5-5-5" /> <path d="M21 12H9" /> <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />',
  'phone': '<path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />',
  'message-circle': '<path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />',
  'messages-square': '<path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /> <path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" />',
  'video': '<path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" /> <rect x="2" y="6" width="14" height="12" rx="2" />',
  'video-off': '<path d="M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196" /> <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" /> <path d="m2 2 20 20" />',
  'mic': '<path d="M12 19v3" /> <path d="M19 10v2a7 7 0 0 1-14 0v-2" /> <rect x="9" y="2" width="6" height="13" rx="3" />',
  'mic-off': '<path d="M12 19v3" /> <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" /> <path d="M16.95 16.95A7 7 0 0 1 5 12v-2" /> <path d="M18.89 13.23A7 7 0 0 0 19 12v-2" /> <path d="m2 2 20 20" /> <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />',
  'volume-2': '<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" /> <path d="M16 9a5 5 0 0 1 0 6" /> <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />',
  'bell': '<path d="M10.268 21a2 2 0 0 0 3.464 0" /> <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />',
  'bell-off': '<path d="M10.268 21a2 2 0 0 0 3.464 0" /> <path d="M17 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 .258-1.742" /> <path d="m2 2 20 20" /> <path d="M8.668 3.01A6 6 0 0 1 18 8c0 2.687.77 4.653 1.707 6.05" />',
  'user': '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /> <circle cx="12" cy="7" r="4" />',
  'user-plus': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /> <circle cx="9" cy="7" r="4" /> <line x1="19" x2="19" y1="8" y2="14" /> <line x1="22" x2="16" y1="11" y2="11" />',
  'user-minus': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /> <circle cx="9" cy="7" r="4" /> <line x1="22" x2="16" y1="11" y2="11" />',
  'circle-user': '<circle cx="12" cy="12" r="10" /> <circle cx="12" cy="10" r="3" /> <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662" />',
  'users': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /> <path d="M16 3.128a4 4 0 0 1 0 7.744" /> <path d="M22 21v-2a4 4 0 0 0-3-3.87" /> <circle cx="9" cy="7" r="4" />',
  'image': '<rect width="18" height="18" x="3" y="3" rx="2" ry="2" /> <circle cx="9" cy="9" r="2" /> <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />',
  'switch-camera': '<path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" /> <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" /> <circle cx="12" cy="12" r="3" /> <path d="m18 22-3-3 3-3" /> <path d="m6 2 3 3-3 3" />',
  'circle-dot': '<circle cx="12" cy="12" r="10" /> <circle cx="12" cy="12" r="1" />',
  'play-circle': '<path d="M9 9.003a1 1 0 0 1 1.517-.859l4.997 2.997a1 1 0 0 1 0 1.718l-4.997 2.997A1 1 0 0 1 9 14.996z" /> <circle cx="12" cy="12" r="10" />',
  'circle': '<circle cx="12" cy="12" r="10" />',
  'settings': '<path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" /> <circle cx="12" cy="12" r="3" />',
  'wrench': '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z" />',
  'pencil': '<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" /> <path d="m15 5 4 4" />',
  'trash-2': '<path d="M10 11v6" /> <path d="M14 11v6" /> <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /> <path d="M3 6h18" /> <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />',
  'clock': '<circle cx="12" cy="12" r="10" /> <path d="M12 6v6l4 2" />',
  'calendar': '<path d="M8 2v4" /> <path d="M16 2v4" /> <rect width="18" height="18" x="3" y="4" rx="2" /> <path d="M3 10h18" />',
  'more-vertical': '<circle cx="12" cy="12" r="1" /> <circle cx="12" cy="5" r="1" /> <circle cx="12" cy="19" r="1" />',
  'more-horizontal': '<circle cx="12" cy="12" r="1" /> <circle cx="19" cy="12" r="1" /> <circle cx="5" cy="12" r="1" />',
  'eye': '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /> <circle cx="12" cy="12" r="3" />',
  'eye-off': '<path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /> <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /> <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /> <path d="m2 2 20 20" />',
  'lock': '<rect width="18" height="11" x="3" y="11" rx="2" ry="2" /> <path d="M7 11V7a5 5 0 0 1 10 0v4" />',
  'globe': '<circle cx="12" cy="12" r="10" /> <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /> <path d="M2 12h20" />',
  'ban': '<circle cx="12" cy="12" r="10" /> <path d="M4.929 4.929 19.07 19.071" />',
  'hand': '<path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" /> <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" /> <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" /> <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />',
  'triangle-alert': '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /> <path d="M12 9v4" /> <path d="M12 17h.01" />',
  'shield-check': '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /> <path d="m9 12 2 2 4-4" />',
  'smile': '<circle cx="12" cy="12" r="10" /> <path d="M8 14s1.5 2 4 2 4-2 4-2" /> <line x1="9" x2="9.01" y1="9" y2="9" /> <line x1="15" x2="15.01" y1="9" y2="9" />',
  'star': '<path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />',
  'flashlight': '<path d="M12 13v1" /> <path d="M17 2a1 1 0 0 1 1 1v4a3 3 0 0 1-.6 1.8l-.6.8A4 4 0 0 0 16 12v8a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-8a4 4 0 0 0-.8-2.4l-.6-.8A3 3 0 0 1 6 7V3a1 1 0 0 1 1-1z" /> <path d="M6 6h12" />',
  'tag': '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" /> <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />',
  'credit-card': '<rect width="20" height="14" x="2" y="5" rx="2" /> <line x1="2" x2="22" y1="10" y2="10" />',
  'building-2': '<path d="M10 12h4" /> <path d="M10 8h4" /> <path d="M14 21v-3a2 2 0 0 0-4 0v3" /> <path d="M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2" /> <path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />',
  'leaf': '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /> <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />',
  'home': '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /> <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />',
  'car': '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" /> <circle cx="7" cy="17" r="2" /> <path d="M9 17h6" /> <circle cx="17" cy="17" r="2" />',
  'facebook': '<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />',
  'linkedin': '<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /> <rect width="4" height="12" x="2" y="9" /> <circle cx="4" cy="4" r="2" />',
  'youtube': '<path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" /> <path d="m10 15 5-3-5-3z" />',
  'instagram': '<rect width="20" height="20" x="2" y="2" rx="5" ry="5" /> <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /> <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />',
  'x-twitter': '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />',
  'github': '<path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />',
  'upload': '<path d="M12 3v12" /> <path d="m17 8-5-5-5 5" /> <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />',
  'hand-metal': '<path d="M18 12.5V10a2 2 0 0 0-2-2a2 2 0 0 0-2 2v1.4" /> <path d="M14 11V9a2 2 0 1 0-4 0v2" /> <path d="M10 10.5V5a2 2 0 1 0-4 0v9" /> <path d="m7 15-1.76-1.76a2 2 0 0 0-2.83 2.82l3.6 3.6C7.5 21.14 9.2 22 12 22h2a8 8 0 0 0 8-8V7a2 2 0 1 0-4 0v5" />',
  'lightbulb': '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /> <path d="M9 18h6" /> <path d="M10 22h4" />',
  'file-text': '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /> <path d="M14 2v5a1 1 0 0 0 1 1h5" /> <path d="M10 9H8" /> <path d="M16 13H8" /> <path d="M16 17H8" />',
  'music': '<path d="M9 18V5l12-2v13" /> <circle cx="6" cy="18" r="3" /> <circle cx="18" cy="16" r="3" />',
  'file': '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /> <path d="M14 2v5a1 1 0 0 0 1 1h5" />',
  'at-sign': '<circle cx="12" cy="12" r="4" /> <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />',
  'mail': '<path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" /> <rect x="2" y="4" width="20" height="16" rx="2" />',
  'hard-drive': '<path d="M10 16h.01" /> <path d="M2.212 11.577a2 2 0 0 0-.212.896V18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5.527a2 2 0 0 0-.212-.896L18.55 5.11A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /> <path d="M21.946 12.013H2.054" /> <path d="M6 16h.01" />',
  'moon': '<path d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />',
  'sun': '<path d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />',
  'maximize': '<path d="M8 3H5a2 2 0 0 0-2 2v3" /> <path d="M21 8V5a2 2 0 0 0-2-2h-3" /> <path d="M3 16v3a2 2 0 0 0 2 2h3" /> <path d="M16 21h3a2 2 0 0 0 2-2v-3" />',
  'minimize': '<path d="M8 3v3a2 2 0 0 1-2 2H3" /> <path d="M21 8h-3a2 2 0 0 1-2-2V3" /> <path d="M3 16h3a2 2 0 0 1 2 2v3" /> <path d="M16 21v-3a2 2 0 0 1 2-2h3" />',
  'sliders-horizontal': '<line x1="21" x2="14" y1="4" y2="4" /> <line x1="10" x2="3" y1="4" y2="4" /> <line x1="21" x2="12" y1="12" y2="12" /> <line x1="8" x2="3" y1="12" y2="12" /> <line x1="21" x2="16" y1="20" y2="20" /> <line x1="12" x2="3" y1="20" y2="20" /> <line x1="14" x2="14" y1="2" y2="6" /> <line x1="8" x2="8" y1="10" y2="14" /> <line x1="16" x2="16" y1="18" y2="22" />',
  'archive': '<rect width="20" height="5" x="2" y="3" rx="1" /> <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" /> <path d="M10 12h4" />',
  'cpu': '<rect width="16" height="16" x="4" y="4" rx="2" /> <rect width="6" height="6" x="9" y="9" rx="1" /> <path d="M15 2v2" /> <path d="M15 20v2" /> <path d="M2 15h2" /> <path d="M2 9h2" /> <path d="M20 15h2" /> <path d="M20 9h2" /> <path d="M9 2v2" /> <path d="M9 20v2" />',
  'terminal': '<polyline points="4 17 10 11 4 5" /> <line x1="12" x2="20" y1="19" y2="19" />',
  'power': '<path d="M12 2v10" /> <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />',
  'sparkles': '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" /> <path d="M20 3v4" /> <path d="M22 5h-4" /> <path d="M4 17v2" /> <path d="M5 18H3" />',
  'undo-2': '<path d="M9 14 4 9l5-5" /> <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />',
  'graduation-cap': '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" /> <path d="M22 10v6" /> <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />',
  'brain': '<path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" /> <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" /> <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" /> <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" /> <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" /> <path d="M3.477 10.896a4 4 0 0 1 .585-.396" /> <path d="M19.938 10.5a4 4 0 0 1 .585.396" /> <path d="M6 18a4 4 0 0 1-1.967-.516" /> <path d="M19.967 17.484A4 4 0 0 1 18 18" />',
  'folder': '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />',
  'clipboard-check': '<rect width="8" height="4" x="8" y="2" rx="1" ry="1" /> <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /> <path d="m9 14 2 2 4-4" />',
  'list': '<line x1="8" x2="21" y1="6" y2="6" /><line x1="8" x2="21" y1="12" y2="12" /><line x1="8" x2="21" y1="18" y2="18" /><line x1="3" x2="3.01" y1="6" y2="6" /><line x1="3" x2="3.01" y1="12" y2="12" /><line x1="3" x2="3.01" y1="18" y2="18" />',
  'list-ordered': '<line x1="10" x2="21" y1="6" y2="6" /><line x1="10" x2="21" y1="12" y2="12" /><line x1="10" x2="21" y1="18" y2="18" /><path d="M4 6h1v4" /><path d="M4 10h2" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />',
  'code': '<polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />',
  'pin': '<path d="M12 17v5" /><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1 1 1 0 0 1 1 1z" />',
  'plug': '<path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" /><path d="M18 8v5a6 6 0 0 1-12 0V8z" />',
  'shopping-cart': '<circle cx="8" cy="21" r="1" /><circle cx="19" cy="21" r="1" /><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />',
  'calculator': '<rect width="16" height="20" x="4" y="2" rx="2" /><line x1="8" x2="16" y1="6" y2="6" /><line x1="16" x2="16" y1="14" y2="18" /><path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" /><path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" />',
  'dumbbell': '<path d="M14.4 14.4 9.6 9.6" /><path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z" /><path d="m21.5 21.5-1.4-1.4" /><path d="M3.9 3.9 2.5 2.5" /><path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z" />',
  'layout-grid': '<rect width="7" height="7" x="3" y="3" rx="1" /> <rect width="7" height="7" x="14" y="3" rx="1" /> <rect width="7" height="7" x="14" y="14" rx="1" /> <rect width="7" height="7" x="3" y="14" rx="1" />',
  'square-user': '<path d="M18 21a6 6 0 0 0-12 0" /> <circle cx="12" cy="11" r="4" /> <rect width="18" height="18" x="3" y="3" rx="2" />',
  'megaphone': '<path d="m3 11 19-9-9 19-2-8-8-2z" />',
  'store': '<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" /><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" /><path d="M2 7h20" /><path d="M22 7v3a2 2 0 0 1-2 2 2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />',
  'bar-chart-2': '<line x1="18" x2="18" y1="20" y2="10" /><line x1="12" x2="12" y1="20" y2="4" /><line x1="6" x2="6" y1="20" y2="14" />',
  'layers': '<path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />',
  'palette': '<circle cx="13.5" cy="6.5" r=".5" fill="currentColor" /><circle cx="17.5" cy="10.5" r=".5" fill="currentColor" /><circle cx="8.5" cy="7.5" r=".5" fill="currentColor" /><circle cx="6.5" cy="12.5" r=".5" fill="currentColor" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />',
  'receipt': '<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><path d="M12 17.5v-11" />',
  'wallet': '<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />',
  'trophy': '<path d="M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978" /> <path d="M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978" /> <path d="M18 9h1.5a1 1 0 0 0 0-5H18" /> <path d="M4 22h16" /> <path d="M6 9H4.5a1 1 0 0 1 0-5H6" /> <path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z" />',
  'circle-plus': '<circle cx="12" cy="12" r="10" /> <path d="M8 12h8" /> <path d="M12 8v8" />',
  'circle-alert': '<circle cx="12" cy="12" r="10" /> <line x1="12" x2="12" y1="8" y2="12" /> <line x1="12" x2="12.01" y1="16" y2="16" />',
  'camera': '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /> <circle cx="12" cy="13" r="3" />',
  'rocket': '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /> <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /> <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /> <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />',
};

/** Map ionicon names → Lucide icon keys (historical template names). */
const NAME_MAP: Record<string, string> = {
  // Navigation
  'chevron-back-outline': 'chevron-left',
  'chevron-forward-outline': 'chevron-right',
  'arrow-back-outline': 'arrow-left',
  'arrow-forward-outline': 'arrow-right',
  'caret-down-outline': 'chevron-down',
  'caret-down': 'chevron-down',
  'caret-forward': 'chevron-right',
  // Actions
  'close': 'x',
  'close-outline': 'x',
  'close-circle': 'x-circle',
  'close-circle-outline': 'x-circle',
  'checkmark': 'check',
  'checkmark-outline': 'check',
  'checkmark-circle': 'check-circle',
  'checkmark-circle-outline': 'check-circle',
  'checkmark-done-outline': 'check-check',
  'add-outline': 'plus',
  'search-outline': 'search',
  'copy-outline': 'copy',
  'download-outline': 'download',
  'refresh-outline': 'refresh-cw',
  'send': 'send',
  'attach-outline': 'paperclip',
  'share-outline': 'share',
  'share-social-outline': 'share-2',
  'open-outline': 'external-link',
  'exit-outline': 'log-out',
  // Communication
  'call': 'phone',
  'call-outline': 'phone',
  'chatbubble-outline': 'message-circle',
  'chatbubbles': 'messages-square',
  'chatbubbles-outline': 'messages-square',
  'videocam': 'video',
  'videocam-outline': 'video',
  'videocam-off': 'video-off',
  'videocam-off-outline': 'video-off',
  'mic': 'mic',
  'mic-outline': 'mic',
  'mic-off': 'mic-off',
  'mic-off-outline': 'mic-off',
  'volume-high-outline': 'volume-2',
  'notifications-outline': 'bell',
  'notifications-off-outline': 'bell-off',
  // People
  'person': 'user',
  'person-outline': 'user',
  'person-add': 'user-plus',
  'person-add-outline': 'user-plus',
  'person-remove-outline': 'user-minus',
  'person-circle': 'circle-user',
  // Presentation mode = single big speaker tile, hence "square-user".
  'person-circle-outline': 'square-user',
  'people': 'users',
  'people-outline': 'users',
  // Media
  'image': 'image',
  'image-outline': 'image',
  'camera-reverse': 'switch-camera',
  'recording': 'circle-dot',
  'recording-outline': 'circle-dot',
  'hard-drive': 'hard-drive',
  'hard-drive-outline': 'hard-drive',
  'play-circle-outline': 'play-circle',
  'radio-button-on': 'circle-dot',
  'radio-outline': 'circle',
  // General UI
  'settings': 'settings',
  'settings-outline': 'settings',
  'options-outline': 'sliders-horizontal',
  'expand-outline': 'maximize',
  'contract-outline': 'minimize',
  'construct': 'wrench',
  'pencil-outline': 'pencil',
  'trash-outline': 'trash-2',
  'trash-bin-outline': 'trash-2',
  'time-outline': 'clock',
  'calendar': 'calendar',
  'calendar-outline': 'calendar',
  'ellipsis-vertical': 'more-vertical',
  'ellipsis-horizontal': 'more-horizontal',
  'eye-outline': 'eye',
  'eye-off-outline': 'eye-off',
  'lock-closed': 'lock',
  'lock-closed-outline': 'lock',
  'globe-outline': 'globe',
  'earth-outline': 'globe',
  'ban': 'ban',
  'ban-outline': 'ban',
  'ban-sharp': 'ban',
  'hand-right': 'hand',
  'hand-left-outline': 'hand-metal',
  'warning-outline': 'triangle-alert',
  'shield-checkmark': 'shield-check',
  'shield-checkmark-outline': 'shield-check',
  'happy-outline': 'smile',
  'star-outline': 'star',
  'flashlight-outline': 'flashlight',
  'pricetag-outline': 'tag',
  'card': 'credit-card',
  'business': 'building-2',
  'leaf': 'leaf',
  'leaf-outline': 'leaf',
  'home': 'home',
  'home-outline': 'home',
  'car-outline': 'car',
  'ellipse': 'circle',
  'bulb-outline': 'lightbulb',
  'moon-outline': 'moon',
  // File types (from getFileIcon)
  'document-text-outline': 'file-text',
  'musical-notes-outline': 'music',
  'document-outline': 'file',
  // Notifications (from getNotificationIcon)
  'at-outline': 'at-sign',
  'mail-outline': 'mail',
  // Social
  'logo-facebook': 'facebook',
  'logo-linkedin': 'linkedin',
  'logo-youtube': 'youtube',
  'logo-instagram': 'instagram',
  'logo-twitter': 'x-twitter',
  'logo-github': 'github',
  'cloud-upload-outline': 'upload',
  // Pin
  'pin-outline': 'pin',
  // Archive
  'archive-outline': 'archive',
  // AI / Agent
  'hardware-chip-outline': 'cpu',
  'school-outline': 'graduation-cap',
  'terminal-outline': 'terminal',
  'power-outline': 'power',
  'sparkles-outline': 'sparkles',
  'arrow-undo-outline': 'undo-2',
  'brain-outline': 'brain',
  'swap-horizontal-outline': 'arrow-left-right',
  'folder-outline': 'folder',
  'folder-open-outline': 'folder-open',
  // Text formatting
  'list-outline': 'list',
  'list-circle-outline': 'list-ordered',
  'code-slash-outline': 'code',
  // Connectors
  'extension-puzzle-outline': 'plug',
  'cart-outline': 'shopping-cart',
  'calculator-outline': 'calculator',
  'fitness-outline': 'dumbbell',
  'grid-outline': 'layout-grid',
  'grid': 'layout-grid',
  'megaphone-outline': 'megaphone',
  'card-outline': 'credit-card',
  'business-outline': 'building-2',
  'logo-wordpress': 'globe',
  'storefront-outline': 'store',
  'analytics-outline': 'bar-chart-2',
  'layers-outline': 'layers',
  'color-palette-outline': 'palette',
  'cube-outline': 'box',
  'receipt-outline': 'receipt',
  'logo-paypal': 'wallet',
  // Arena
  'trophy-outline': 'trophy',
  'rocket-outline': 'rocket',
  // Stragglers (previously rendered blank)
  'add-circle-outline': 'circle-plus',
  'alert-circle-outline': 'circle-alert',
  'camera-outline': 'camera',
  'chevron-down-outline': 'chevron-down',
};

const FILLED_ICONS = new Set(['facebook', 'github']);
/* eslint-enable @typescript-eslint/naming-convention, max-len */

const wrap = (key: string, inner: string): string => {
  const isFilled = FILLED_ICONS.has(key);
  const attrs = 'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"'
    + (isFilled ? ' fill="currentColor"' : ' fill="none" stroke="currentColor"'
    + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round"');
  return `<svg ${attrs}>${inner}</svg>`;
};

/**
 * Heroicons outline from `@crewdle/ui-kit/icons` (raw `<svg>` constants,
 * exactly what provideCrwdIcons documents; same names and glyphs as the
 * `@ng-icons/heroicons` package this used to import). These are the names the chat
 * surface has always used; keeping them means the ng-icon → crwd-icon swap
 * changed the rendering component without changing a single glyph.
 *
 * The two glyph sets living side by side (lucide on the agent side, heroicons
 * on the chat side) is a REMAINING inconsistency, not the intended end state —
 * unifying them is a design call, not a mechanical one.
 */
const HERO_ICONS: Record<string, string> = {
  heroAdjustmentsHorizontal,
  heroArchiveBox,
  heroArrowDownTray,
  heroArrowPath,
  heroArrowRightStartOnRectangle,
  heroArrowUp,
  heroArrowUturnLeft,
  heroArrowsPointingOut,
  heroBars2,
  heroBars3BottomLeft,
  heroBeaker,
  heroBolt,
  heroBookOpen,
  heroBookmark,
  heroBookmarkSlash,
  heroCamera,
  heroChatBubbleLeftRight,
  heroChatBubbleOvalLeftEllipsis,
  heroCheck,
  heroCheckCircle,
  heroChevronDown,
  heroChevronLeft,
  heroChevronRight,
  heroChevronUp,
  heroClipboard,
  heroClipboardDocument,
  heroClock,
  heroComputerDesktop,
  heroDocument,
  heroDocumentDuplicate,
  heroDocumentText,
  heroEllipsisHorizontal,
  heroEllipsisVertical,
  heroExclamationTriangle,
  heroEye,
  heroEyeSlash,
  heroFolder,
  heroFolderMinus,
  heroFolderOpen,
  heroFolderPlus,
  heroGlobeAlt,
  heroInformationCircle,
  heroLinkSlash,
  heroMagnifyingGlass,
  heroPaperClip,
  heroPause,
  heroPencil,
  heroPencilSquare,
  heroPlay,
  heroPlus,
  heroShare,
  heroSparkles,
  heroSquares2x2,
  heroStop,
  heroTrash,
  // Chat surface: folder sharing (people glyph) and the agent-drive knowledge tile.
  heroUsers,
  heroCpuChip,
  heroUserCircle,
  heroWindow,
  heroXMark,
};

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Connect standardizes on Heroicons (the ui-kit's documented set, matching the
 * chat surface). Re-point each Lucide key to its Heroicon SVG here; because the
 * ionicon aliases (NAME_MAP) copy `icons[lucideKey]`, every alias follows with
 * no template edits. Keys with no acceptable Heroicon — brain, check-check,
 * circle, dumbbell, pin, and the github/linkedin brand logos — are omitted and
 * stay Lucide.
 */
const HERO_FOR_LUCIDE: Record<string, string> = {
  archive: heroArchiveBox,
  'arrow-left-right': heroArrowsRightLeft,
  ban: heroNoSymbol,
  'bar-chart-2': heroChartBar,
  bell: heroBell,
  'bell-off': heroBellSlash,
  'building-2': heroBuildingOffice,
  calculator: heroCalculator,
  calendar: heroCalendar,
  check: heroCheck,
  'check-circle': heroCheckCircle,
  'chevron-down': heroChevronDown,
  'chevron-left': heroChevronLeft,
  'chevron-right': heroChevronRight,
  'circle-alert': heroExclamationCircle,
  'circle-plus': heroPlusCircle,
  'clipboard-check': heroClipboardDocumentCheck,
  clock: heroClock,
  code: heroCodeBracket,
  copy: heroClipboard,
  cpu: heroCpuChip,
  'credit-card': heroCreditCard,
  download: heroArrowDownTray,
  'external-link': heroArrowTopRightOnSquare,
  eye: heroEye,
  file: heroDocument,
  'file-text': heroDocumentText,
  folder: heroFolder,
  'folder-open': heroFolderOpen,
  globe: heroGlobeAlt,
  'graduation-cap': heroAcademicCap,
  'hard-drive': heroServer,
  image: heroPhoto,
  layers: heroRectangleStack,
  'layout-grid': heroSquares2x2,
  lightbulb: heroLightBulb,
  list: heroListBullet,
  'list-ordered': heroNumberedList,
  lock: heroLockClosed,
  'log-out': heroArrowRightStartOnRectangle,
  mail: heroEnvelope,
  maximize: heroArrowsPointingOut,
  megaphone: heroMegaphone,
  'message-circle': heroChatBubbleOvalLeft,
  'messages-square': heroChatBubbleLeftRight,
  minimize: heroArrowsPointingIn,
  'more-vertical': heroEllipsisVertical,
  music: heroMusicalNote,
  palette: heroSwatch,
  paperclip: heroPaperClip,
  pencil: heroPencil,
  'play-circle': heroPlayCircle,
  plus: heroPlus,
  receipt: heroReceiptPercent,
  'refresh-cw': heroArrowPath,
  search: heroMagnifyingGlass,
  send: heroPaperAirplane,
  settings: heroCog6Tooth,
  'share-2': heroShare,
  'shield-check': heroShieldCheck,
  'shopping-cart': heroShoppingCart,
  smile: heroFaceSmile,
  sparkles: heroSparkles,
  store: heroBuildingStorefront,
  'trash-2': heroTrash,
  'triangle-alert': heroExclamationTriangle,
  upload: heroArrowUpTray,
  'user-plus': heroUserPlus,
  users: heroUsers,
  video: heroVideoCamera,
  wallet: heroWallet,
  wrench: heroWrench,
  x: heroXMark,
  'x-circle': heroXCircle,
};

const HERO_GAME_CONTROLLER = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"'
  + ' aria-hidden="true" data-slot="icon" style="stroke-width:var(--ng-icon__stroke-width, 1.5)">'
  + '<path stroke-linecap="round" stroke-linejoin="round" d="M7.5 6.75h9a4.5 4.5 0 0 1 4.41 3.62l1.1 5.5a2.63 2.63 0 0 1-4.6 2.2'
  + 'l-1.66-1.82H8.25l-1.66 1.82a2.63 2.63 0 0 1-4.6-2.2l1.1-5.5A4.5 4.5 0 0 1 7.5 6.75Z" />'
  + '<path stroke-linecap="round" stroke-linejoin="round" d="M8.25 9.75v3M6.75 11.25h3M15 11.25h.008v.008H15v-.008ZM17.25 11.25h.008v.008h-.008v-.008Z" />'
  + '</svg>';

// The sidebar Help button's glyph (crwd-sidebar, @crewdle/ui-kit, 2026-10-31): a
// speech bubble holding a "?". The kit draws it inline and does not export it,
// so the same paths are registered here for the onboarding tour's Help slide.
const HELP_BUBBLE = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"'
  + ' aria-hidden="true" data-icon="help-bubble">'
  + '<path stroke-linecap="round" stroke-linejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023'
  + ' 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />'
  + '<path stroke-linecap="round" stroke-linejoin="round" d="M10.4 9.75c.33-.76 1-1.25 1.85-1.25 1.04 0 1.85.73 1.85 1.65 0 .78-.52 1.2-1.13 1.53'
  + '-.42.23-.72.55-.72 1.02v.3" />'
  + '<path stroke-linecap="round" stroke-linejoin="round" d="M12.25 15.25h.008v.008h-.008z" />'
  + '</svg>';

// Names absent from LUCIDE_ICONS/NAME_MAP that were rendering blank — repaired
// straight to Heroicons (used by integration cards + a few static templates).
const HERO_DIRECT: Record<string, string> = {
  // Marketplace industry icons (2026-10-23) — direct, for the same reason as below.
  'heart-outline': heroHeart,
  'construct-outline': heroWrenchScrewdriver,
  'ticket-outline': heroTicket,
  'scale-outline': heroScale,
  'gift-outline': heroGift,
  'briefcase-outline': heroBriefcase,
  // The Local files connector's icon (integration.model.ts). 'folder-open' is not a
  // LUCIDE_ICONS key, so the NAME_MAP alias 'folder-open-outline' → 'folder-open'
  // never resolved and the row rendered blank (2026-10-01). Register it directly.
  'folder-open-outline': heroFolderOpen,
  'cash-outline': heroBanknotes,
  'mail-open-outline': heroEnvelopeOpen,
  'pause-circle-outline': heroPauseCircle,
  // The Roblox connector's icon (integration.model.ts). Heroicons ships no game
  // controller, so this is a hand-drawn glyph in the Heroicons outline format
  // (24 px grid, 1.5 stroke, round caps/joins, same svg attributes).
  'game-controller-outline': HERO_GAME_CONTROLLER,
  // The Marketplace Restaurants industry icon (listMarketplaceTemplates
  // industries[].icon). Heroicons ships no fork & knife — the storefront reads as the venue.
  'restaurant-outline': heroBuildingStorefront,
  'help-bubble': HELP_BUBBLE,
};
/* eslint-enable @typescript-eslint/naming-convention */

export const APP_ICONS: Record<string, string> = (() => {
  const icons: Record<string, string> = {};
  for (const [key, inner] of Object.entries(LUCIDE_ICONS)) {
    icons[key] = HERO_FOR_LUCIDE[key] ?? wrap(key, inner);
  }
  for (const [alias, key] of Object.entries(NAME_MAP)) {
    if (icons[key]) {
      icons[alias] = icons[key];
    }
  }
  return { ...icons, ...HERO_DIRECT, ...HERO_ICONS };
})();
